import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Order } from '../types';
import { fetchOrder } from './index'; // Ensure this function exists in your API file
import Spinner from '../components/Spinner';

const OrderTracking = () => {
  const [orderId, setOrderId] = useState('');
  const [orderStatus, setOrderStatus] = useState<Order | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setHasSearched(true);
    setError('');
    setOrderStatus(null);
    
    const trimmedId = orderId.trim();

    try {
      // Fetch order from API
      const { data } = await fetchOrder(trimmedId);
      setOrderStatus(data);
    } catch (err: any) {
      console.error(err);
      
      // Fallback error message
      const errorMsg = err.response?.data?.message || 'Order not found.';
      setError(`${errorMsg} Please check the ID and try again.`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Delivered': return 'bg-green-100 text-green-800';
      case 'Dispatched': return 'bg-blue-100 text-blue-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const hasError = error !== ''; // true when there is an error
  const isLoading = loading === true; // true when loading

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-serif font-bold text-primary">Track Your Order</h1>
          <p className="mt-2 text-gray-600 font-sans">Enter your Order ID to see the current status.</p>
        </div>

        {/* Search Form */}
        <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-100 mb-8">
          <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-4">
            <input 
              type="text" 
              placeholder="e.g. MFP-123456"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              className="flex-1 appearance-none border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-primary focus:border-primary font-sans
              placeholder:italic
              "
              // Example validation: at least 7 chars that start with `MFP-`
              minLength={7}
              pattern={`MFP-.*`}
              required
            />
            <button 
              type="submit" 
              disabled={loading}
              className="bg-primary text-white px-6 py-3 rounded-md font-medium hover:bg-green-800 transition shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? <><Spinner /> Tracking...</> : 'Track Order'}
            </button>
          </form>
          {/* Only show error message if there is actually an error */}
          {hasError && <p className="mt-3 text-sm text-red-600 font-sans">{error}</p>}
        </div>

        {/* Improve: Conditionally render order details/no order message/make order message */}
        {orderStatus ? (
          <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-100 animate-fade-in">
            <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 font-serif">Order Details</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">ID: {orderStatus?.id}</p>
              </div>
              <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getStatusColor(orderStatus.status)}`}>
                {orderStatus.status}
              </span>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
            {/* Another check for if order status is null to prevent errors */}
            {!orderStatus ?
            (<div className="text-center">
                <p>Loading Order...</p>
            </div>)
            :

              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Items</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 whitespace-pre-line">{orderStatus.items}</dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Date Placed</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{orderStatus.date}</dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Total Amount</dt>
                  <dd className="mt-1 text-sm font-bold text-primary sm:mt-0 sm:col-span-2">₦{orderStatus.total.toLocaleString()}</dd>
                </div>

              </dl>
}

            </div>
          </div>
        ) : (
            !hasError && !isLoading && hasSearched ?
          (
            <div className="text-center">
            {/* Improve: Direct User to make an order! */}
              <p>No order found. <Link to="/">Make an order today!</Link></p>
            </div>)
            :
            (<></>)
        )}
      </div>
    </div>
  );
};


export default OrderTracking;